/**
 * What this device remembers for the gate (docs/members.md "Personal passwords"): whether it has
 * ever signed in as a person (their own password or a passkey, or set one), and the held names it
 * has seen. The gate leads with "Sign in" only then; everyone else meets the household password
 * first, which is the door a newcomer has. Free of Preact and the DOM beyond localStorage, so
 * Node's test runner can check it (tests/gate-memory.test.ts).
 */
import { heldNameFor } from "../shared/passwords.ts";

const PERSON_KEY = "op.signedInAsPerson";
const HELD_KEY = "op.heldNames";
/** Enough for any household's held names; the oldest drop first. */
const HELD_MAX = 40;

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode etc. */
  }
}

/** Records that this device has been signed in as a person (AuthState.person was set). */
export function rememberPersonSignIn(): void {
  write(PERSON_KEY, "1");
}

export function hasSignedInAsPerson(): boolean {
  return read(PERSON_KEY) === "1";
}

/** The held names this device has been told about (GET /api/peons `held`, a 403 name_held). */
export function knownHeldNames(): string[] {
  try {
    const parsed = JSON.parse(read(HELD_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === "string") : [];
  } catch {
    return [];
  }
}

export function rememberHeldNames(names: readonly string[]): void {
  if (names.length === 0) return;
  const merged = [...new Set([...knownHeldNames().filter((n) => !names.includes(n)), ...names])].slice(-HELD_MAX);
  write(HELD_KEY, JSON.stringify(merged));
}

/** Whether `name` is a held name this device knows of (ignoring case and spaces, as the server does). */
export function isKnownHeld(name: string | null | undefined): boolean {
  if (!name) return false;
  return heldNameFor(knownHeldNames().map((n) => ({ name: n })), name) !== null;
}

/** The gate's first form: "Sign in" (name and own password) for a device that has been a person, else the household password. */
export function gateLeadsWithPerson(name: string | null | undefined): boolean {
  return hasSignedInAsPerson() || isKnownHeld(name);
}
